import {
  ByCategoryCategoryContentElementContentModel,
  ByCategoryCategoryContentPropertiesModel,
  CategoryContentResponseModel,
} from '@/services/platform/api/model';
import { cn, ImagesToImageMap, LinksToLinkMap, ModuleProperties, useLocaleStore } from '@/lib/core';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { ShopDiscoverBanner } from '@/components/shared/shop';
import { useEffect, useState } from 'react';
import { getCategoryById } from '@/services/platform/data/shop/catalog';

export const ByCategoryCategoryContent = (props: ModuleProperties) => {
  const content = props.content as ByCategoryCategoryContentElementContentModel | undefined;
  const properties = content?.properties as ByCategoryCategoryContentPropertiesModel;
  const locale = useLocaleStore(state => state.locale);
  const pickedCategory = properties?.categoryPicker?.[0] as CategoryContentResponseModel | undefined;

  // Hooks run before any early return — see the note below the state declaration.
  const [category, setCategory] = useState<CategoryContentResponseModel | undefined>(
    pickedCategory?.properties ? pickedCategory : undefined
  );

  useEffect(() => {
    if (!pickedCategory?.id) return;
    getCategoryById(pickedCategory.id, locale).then(result => {
      if (result) setCategory(result);
    });
  }, [pickedCategory?.id]);

  /*
   * `if (!content) return null` used to sit above these hooks (and again below them). A component
   * that returns before calling its hooks renders a different number of them per pass, which is what
   * React throws "Rendered fewer hooks than expected" over the moment `content` goes from set to
   * unset. Nothing here needs `content`, so the guard simply moves after them.
   */
  if (!content) return null;

  const bannerTitle = properties?.title;
  const bannerDescription = properties?.description;
  const bannerLink = LinksToLinkMap(properties?.link ?? undefined);
  const bannerImage = ImagesToImageMap(category?.properties?.image);

  /*
   * The id comes from the CMS picker, not from the fetched category — `category` is undefined until
   * `getCategoryById` resolves, and interpolating that produces the string `"undefined"` and a real
   * `/category_undefined` link. Same defect and same fix as `byCategoryCollectionContent`.
   */
  const categoryId = category?.id ?? pickedCategory?.id;
  if (bannerLink && categoryId) {
    bannerLink.href = `${props?.context?.page?.route?.path}/category_${categoryId}`;
  }

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        /*
         * `group` lets ShopDiscoverBanner react to this section's own position (`group-last:` —
         * see that component) rather than the always-single-child position inside its own
         * immediate parent, which is `:last-child` regardless of where the module sits.
         *
         * The `last:mb-[...]` on top of `mb-1.25` only fires when this module is the very last
         * one on the page: it cancels out DefaultScreenLayout's own trailing `pb-safe-area`
         * (`max(1rem, env(safe-area-inset-bottom))` — see components/shared/screen/dispatcher.tsx)
         * so the page ends with the same 5px (`mb-1.25`) gap used between modules, instead of
         * stacking that gap on top of the page's own bottom padding. Depends on `pb-safe-area`
         * keeping that exact formula — revisit if it changes.
         */
        base: cn([
          'module__by-category-category-content group mb-1.25',
          'last:mb-[calc(0.3125rem-max(1rem,env(safe-area-inset-bottom)))]!',
        ]),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ShopDiscoverBanner
        image={bannerImage}
        title={bannerTitle}
        description={bannerDescription}
        link={bannerLink}
      />
    </ModuleWrapper>
  );
};
